/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';

import {
    object, bool, string, func, array,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import CheckCircle from '@material-ui/icons/CheckCircle';
import Info from '@material-ui/icons/Info';
import Cancel from '@material-ui/icons/Cancel';

const styles = (theme) => {
    const { palette } = theme;
    return ({
        rootFormControl: {
            border: '0',
            borderBottom: `solid 1px ${palette.colorNeutral60}`,
            width: '100%',
            padding: '0 0 3px 0',
            marginBottom: '8px',
            '& select:-webkit-autofill, & select:-webkit-autofill:hover, & select:-webkit-autofill:focus': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
                borderBottom: `solid 2px ${palette.colorNeutral90}`,
            },
        },
        rootCheckCircleIcon: {
            color: palette.colorSuccess,
            padding: '0',
            maxWidth: '16px',
            position: 'absolute',
            top: '-5px',
        },
        rootErrorIcon: {
            color: palette.error.light,
        },
        rootInputLabel: {
            fontSize: '14px',
            fontWeight: '500',
            textTransform: 'uppercase',
            paddingLeft: '0',
            paddingTop: '0',
            lineHeight: '14px',
            top: '3px',
        },
        asteriskInputLabel: {
            margin: '0',
            fontSize: '20px',
        },
        rootFormHelperText: {
            margin: '0px',
            padding: '3px 10px',
        },
        formHelperText: {
            fontSize: '12px',
            color: palette.white,
        },
        errorColor: {
            backgroundColor: palette.colorAlert,
        },
        infoColor: {
            backgroundColor: palette.colorNeutral90,
        },
        infoArrow: {
            margin: '0px 0px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: `5px solid ${palette.colorNeutral60}`,
            position: 'absolute',
            right: '18px',
            top: '47px',
        },
        rootNativeSelect: {
            padding: '2px 10px',
        },
        selectNativeSelect: {
            padding: '3px 38px 3px 0',
            color: palette.colorNeutral60,
            '&:active, &:hover, &:focus': {
                backgroundColor: theme.palette.white,
            },
        },
        positionEndInputAdornment: {
            marginLeft: '0px',
        },
    });
};

const CustomInputField = (props) => {
    const {
        fieldId,
        name,
        label,
        classes,
        showInfo,
        isRequired,
        autoComplete,
        handleChange,
        handleBlur,
        handleOnFocus,
        handleOnKeyUp,
        val,
        flagError,
        errorMessage,
        infoHelpText,
        disabled,
        dropdown,
        toggleDisplayFieldInfo,
    } = props;

    const endAdornment = (infoHelpText) ? (
        <InputAdornment position="end">
            <IconButton
                onClick={toggleDisplayFieldInfo}
            >
                {infoHelpText && (showInfo ? <Cancel /> : <Info />)}
            </IconButton>
        </InputAdornment>
    ) : null;

    const options = dropdown.map((item) => <option key={item.key} value={item.key}>{item.value}</option>);

    return (
        <FormControl
            classes={{
                root: classes.rootFormControl,
            }}
            error={flagError}
        >
            <InputLabel
                classes={{
                    root: classes.rootInputLabel,
                }}
                shrink
                htmlFor={name}
            >
                <span>
                    <span>{val ? label : ''}</span>
                    {isRequired && val && <span className={classes.asteriskInputLabel}>*</span>}
                    {!flagError && isRequired && val && (
                        <CheckCircle classes={{ root: classes.rootCheckCircleIcon }} />
                    )}
                </span>
            </InputLabel>
            <NativeSelect
                key={`tf_input_${fieldId}`}
                classes={{
                    root: classes.rootNativeSelect,
                    select: classes.selectNativeSelect,
                }}
                disableUnderline
                inputProps={{
                    name,
                    id: fieldId,
                    autoComplete,
                    disabled,
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleOnFocus}
                onKeyUp={handleOnKeyUp}
                value={val}
                error={flagError}
                data-test={`checkout-text-field-div-wrapper-${name}`}
                endAdornment={endAdornment}
                variant="outlined"
            >
                {options}
            </NativeSelect>
            {showInfo && (
                <FormHelperText
                    id={`helper-text-${fieldId}`}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    classes={{
                        root: `${classes.rootFormHelperText} ${classes.infoColor}`,
                    }}
                >
                    <span className={classes.infoArrow} />
                    <span className={classes.formHelperText}>
                        {infoHelpText}
                    </span>
                </FormHelperText>
            )}
            {flagError && (
                <FormHelperText
                    id={`helper-text-${fieldId}`}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    classes={{
                        root: `${classes.rootFormHelperText} ${classes.errorColor}`,
                    }}
                >
                    <span className={classes.formHelperText}>
                        {errorMessage}
                    </span>
                </FormHelperText>
            )}
        </FormControl>
    );
};

CustomInputField.defaultProps = {
    label: '',
    fieldId: '',
    classes: {},
    infoHelpText: '',
    showInfo: false,
    isRequired: false,
    name: '',
    val: '',
    errorMessage: null,
    flagError: null,
    disabled: false,
    autoComplete: 'off',
    handleOnFocus: () => { },
    handleOnKeyUp: () => { },
    handleChange: () => {},
    handleBlur: () => {},
    toggleDisplayFieldInfo: () => {},
    dropdown: [],
};

CustomInputField.propTypes = {
    label: string,
    fieldId: string,
    classes: object,
    infoHelpText: string,
    showInfo: bool,
    isRequired: bool,
    val: string,
    name: string,
    handleOnFocus: func,
    handleOnKeyUp: func,
    errorMessage: string,
    flagError: bool,
    disabled: bool,
    autoComplete: string,
    handleChange: func,
    handleBlur: func,
    dropdown: array,
    toggleDisplayFieldInfo: func,
};

export default withStyles(styles)(CustomInputField);
